import * as React from 'react';
import { getPartitionedNativeProps, slot, useFluent, useId, useMergedRefs } from '@fluentui/react-components';
import type { EditorInputProps, EditorInputState } from './EditorInput.types';
import { SENTINEL_VALUE } from '@fluentui-copilot/chat-input-plugins';
import { $createTextNode, $insertNodes } from '@fluentui-copilot/react-text-editor';
import { $getRoot } from '@fluentui-copilot/react-text-editor';
import { LexicalPlainTextPlugin } from '@fluentui-copilot/react-text-editor';
import { mergeCallbacks } from '@fluentui/react-utilities';
import { useLexicalContentEditable } from '../../utilities/useLexicalContentEditable';

/**
 * Create the state required to render EditorInput.
 *
 * The returned state can be modified with hooks such as useEditorInputStyles_unstable,
 * before being passed to renderEditorInput_unstable.
 *
 * @param props - props from this instance of EditorInput
 * @param ref - reference to root HTMLElement of EditorInput
 */
export const useEditorInput_unstable = (props: EditorInputProps, ref: React.Ref<HTMLSpanElement>): EditorInputState => {
  const {
    onChange,
    onPaste,
    trimWhiteSpace = false,
    textPlugin = LexicalPlainTextPlugin,
    history = true,
    editorRef,
    isSentinelNodeEnabled = true,
  } = props;
  const { root, primary } = getPartitionedNativeProps({
    primarySlotTagName: 'span',
    props,
    excludedPropNames: ['onChange', 'onPaste', 'defaultValue'],
  });

  const handleOnChange = React.useCallback(
    (newValue: string) => {
      onChange?.({}, { value: newValue });
    },
    [onChange],
  );

  const { canShowPlaceholder, spanRef, disabled, lexicalEditor, lexicalInitialConfig } = useLexicalContentEditable({
    customNodes: props.customNodes,
    defaultValue: useDefaultValueFunction(props),
    disabled: props.disabled,
  });

  const placeholderValue = canShowPlaceholder
    ? slot.optional(props.placeholderValue, { elementType: 'span' })
    : undefined;
  const placeholderId = useId('chat-input-placeholder', placeholderValue?.id);
  if (placeholderValue) {
    placeholderValue.id = placeholderId;
  }
  const describedBy = primary['aria-describedby'] ? `${primary['aria-describedby']} ${placeholderId}` : placeholderId;

  return {
    components: {
      root: 'span',
      input: 'span',
      placeholderValue: 'span',
    },
    root: slot.always(props.root, { defaultProps: { ...root }, elementType: 'span' }),
    input: slot.always(props.input, {
      defaultProps: {
        ref: useMergedRefs(ref, spanRef),
        role: 'textbox',
        contentEditable: !disabled,
        'aria-readonly': disabled ? true : undefined,
        disabled,
        suppressContentEditableWarning: true,
        tabIndex: disabled ? undefined : 0,
        ...primary,
        'aria-describedby': describedBy,
      },
      elementType: 'span',
    }),
    placeholderValue,
    lexicalInitialConfig,
    editorRef,
    disabled,
    textPlugin,
    onPaste,
    handleOnChange,
    trimWhiteSpace,
    history,
    isSentinelNodeEnabled,
    lexicalEditor,
  };
};

function useDefaultValueFunction(props: EditorInputProps) {
  const { targetDocument } = useFluent();
  const requestAnimationFrame = targetDocument?.defaultView?.requestAnimationFrame;

  const [defaultValueFunction] = React.useState(() => {
    const { defaultValue, onDefaultValueSet } = props;
    if (defaultValue === undefined) {
      return undefined;
    }

    const insertDefaultValueFunction =
      typeof defaultValue === 'function'
        ? defaultValue
        : () => {
            $insertNodes([$createTextNode(defaultValue)]);
          };

    // After the defaultValue function has run, read back the result and give
    // it to the onDefaultValueSet callback.
    const readDefaultValueFunction = onDefaultValueSet
      ? () => {
          const text = $getRoot().getTextContent().replace(SENTINEL_VALUE, '');

          // This runs while rendering `LexicalComposer` so delay calling back into the parent
          // until after finished rendering `LexicalComposer`
          requestAnimationFrame?.(() => {
            onDefaultValueSet?.(text);
          });
        }
      : undefined;

    return mergeCallbacks(insertDefaultValueFunction, readDefaultValueFunction);
  });

  return defaultValueFunction;
}
